import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import VideocamIcon from "@mui/icons-material/Videocam";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import PlaceIcon from "@mui/icons-material/Place";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import videoImg from "../assets/video.png";

const isAudioUrl = (url = "") => {
  return (
    /\.(mp3|wav|m4a|aac|ogg|oga)(?:[?#].*)?$/i.test(url) ||
    url.includes("audio") ||
    url.includes("voice")
  );
};

const isVideoUrl = (url = "") => {
  return (
    /\.(mp4|webm|mov|m4v|avi|mkv|3gp)(?:[?#].*)?$/i.test(url) ||
    url.includes("video")
  );
};

export const getSubmissionType = (item = {}) => {
  if (item?.type === "location" || item?.title === "Location Shared") {
    return "location";
  }
  const mediaUrl = item?.videoUrl || item?.audioUrl || item?.url || "";
  if (!mediaUrl && (item?.newCurrentLat != null || item?.latitude != null || item?.lat != null)) {
    return "location";
  }
  if (item?.type === "audio" || isAudioUrl(mediaUrl)) {
    return "audio";
  }
  if (item?.type === "video" || isVideoUrl(mediaUrl)) {
    return "video";
  }
  return mediaUrl ? "media" : "location";
};

export default function File(props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeMediaUrl, setActiveMediaUrl] = useState(null);

  // Collect submissions from various possible keys (videos, audios, locations, meta)
  const rawSubmissions =
    props?.props?.videos ||
    props?.videos ||
    props?.props?.meta?.videos ||
    props?.meta?.videos ||
    props?.props?.savedLocations ||
    props?.savedLocations ||
    props?.props?.locations ||
    props?.locations ||
    {};

  const submissionArray = Array.isArray(rawSubmissions)
    ? rawSubmissions
    : Object.values(rawSubmissions || {});

  const count = submissionArray.length;

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setActiveMediaUrl(null);
  };

  if (count === 0) {
    return null;
  }

  return (
    <div>
      <Button
        onClick={handleClick}
        variant="contained"
        size="small"
        sx={{
          bgcolor: "#dc2626",
          color: "#fff",
          fontSize: "0.75rem",
          fontWeight: 600,
          textTransform: "none",
          py: 0.4,
          px: 1.4,
          borderRadius: "6px",
          "&:hover": { bgcolor: "#b91c1c" },
        }}
      >
        Open ({count})
      </Button>

      {isModalOpen && (
        <Dialog
          open={isModalOpen}
          onClose={handleClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { borderRadius: "12px", p: 1 },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ color: "#1e293b" }}>
                Wellbeing Check Submissions
              </Typography>
              <Chip
                label={`${count} ${count === 1 ? "entry" : "entries"}`}
                size="small"
                sx={{ bgcolor: "#fee2e2", color: "#b91c1c", fontWeight: 700 }}
              />
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 2 }}>
            <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e2e8f0" }}>
              <Table aria-label="submissions table">
                <TableHead sx={{ bgcolor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Type
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Latitude
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Longitude
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Address / Map
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Media / Content
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, color: "#475569" }}>
                      Time
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissionArray.map((item, index) => {
                    const lat = item?.newCurrentLat ?? item?.latitude ?? item?.lat ?? "—";
                    const lng = item?.newCurrentLong ?? item?.longitude ?? item?.lng ?? "—";
                    const mediaUrl = item?.videoUrl || item?.audioUrl || item?.url || "";
                    const type = getSubmissionType(item);
                    const hasCoords = lat !== "—" && lng !== "—";
                    const mapUrl = hasCoords ? `https://www.google.com/maps?q=${lat},${lng}` : null;
                    const dateStr = item?.createdAt || item?.timestamp
                      ? new Date(item.createdAt || item.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                        })
                      : "—";

                    return (
                      <TableRow key={index} hover sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                        {/* Type */}
                        <TableCell align="center" sx={{ padding: "10px" }}>
                          {type === "location" ? (
                            <Chip
                              icon={<PlaceIcon fontSize="small" />}
                              label="Location"
                              size="small"
                              sx={{ bgcolor: "#e0f2fe", color: "#0369a1", fontWeight: 700 }}
                            />
                          ) : type === "audio" ? (
                            <Chip
                              icon={<AudiotrackIcon fontSize="small" />}
                              label="Audio"
                              size="small"
                              sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 700 }}
                            />
                          ) : (
                            <Chip
                              icon={<VideocamIcon fontSize="small" />}
                              label="Video"
                              size="small"
                              sx={{ bgcolor: "#fee2e2", color: "#b91c1c", fontWeight: 700 }}
                            />
                          )}
                        </TableCell>

                        {/* Latitude */}
                        <TableCell align="center" sx={{ padding: "10px", fontWeight: 600 }}>
                          {lat}
                        </TableCell>

                        {/* Longitude */}
                        <TableCell align="center" sx={{ padding: "10px", fontWeight: 600 }}>
                          {lng}
                        </TableCell>

                        {/* Address / Google Maps Link */}
                        <TableCell align="center" sx={{ padding: "10px" }}>
                          {mapUrl ? (
                            <Button
                              variant="outlined"
                              size="small"
                              href={mapUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              endIcon={<OpenInNewIcon fontSize="small" />}
                              sx={{
                                textTransform: "none",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                py: 0.3,
                                px: 1.2,
                                borderColor: "#cbd5e1",
                                color: "#1e40af",
                              }}
                            >
                              Open in Maps
                            </Button>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        {/* Media / Content */}
                        <TableCell align="center" sx={{ padding: "10px" }}>
                          {type === "audio" && mediaUrl ? (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                              <audio controls src={mediaUrl} style={{ width: "190px", height: "32px" }} />
                              <a
                                href={mediaUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontSize: "0.72rem", color: "#1e40af", textDecoration: "underline" }}
                              >
                                Direct Audio Link
                              </a>
                            </Box>
                          ) : type === "video" && mediaUrl ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                              <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={videoImg}
                                  alt="video-thumbnail"
                                  style={{
                                    width: "42px",
                                    height: "42px",
                                    cursor: "pointer",
                                    borderRadius: "4px",
                                  }}
                                />
                              </a>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => setActiveMediaUrl(mediaUrl)}
                                sx={{ textTransform: "none", fontSize: "0.75rem", color: "#dc2626", fontWeight: 600 }}
                              >
                                Play
                              </Button>
                            </Box>
                          ) : type === "location" ? (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                              <PlaceIcon sx={{ color: "#0284c7", fontSize: "1.2rem" }} />
                              <Typography variant="caption" sx={{ fontWeight: 600, color: "#0369a1" }}>
                                {item?.title || item?.feedback || "Location Shared"}
                              </Typography>
                            </Box>
                          ) : mediaUrl ? (
                            <a
                              href={mediaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#1e40af", fontWeight: 600, fontSize: "0.8rem" }}
                            >
                              View Media
                            </a>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        {/* Timestamp */}
                        <TableCell align="center" sx={{ padding: "10px", fontSize: "0.75rem", color: "#64748b" }}>
                          {dateStr}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Video Inline Preview if Play clicked */}
            {activeMediaUrl && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: "#0f172a", borderRadius: "8px", textAlign: "center" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography variant="caption" sx={{ color: "#94a3b8", fontWeight: 600 }}>
                    Video Preview
                  </Typography>
                  <IconButton size="small" onClick={() => setActiveMediaUrl(null)} sx={{ color: "#fff" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
                <video
                  controls
                  autoPlay
                  src={activeMediaUrl}
                  style={{ width: "100%", maxHeight: "320px", borderRadius: "6px" }}
                />
              </Box>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 1.5 }}>
            <Button onClick={handleClose} variant="contained" sx={{ color: "#fff", bgcolor: "#334155" }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}
